// src/features/risks/hooks/useRiskHistory.ts
import { useEffect, useState } from "react"
import { useAppDispatch } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { Risk } from "@/features/risks/entities/risk/model/risk.types"

/**
 * Hook to fetch and manage risk history.
 * Returns:
 *  - `history`: full array of Risk versions
 *  - `hasHistory`: true if there are previous versions (length > 1)
 *  - `loading`: fetch status
 */
export function useRiskHistory(riskId?: string, establishmentId?: string | null) {
  const dispatch = useAppDispatch()
  const [history, setHistory] = useState<Risk[]>([])
  const [hasHistory, setHasHistory] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!riskId || !establishmentId) return

    let isMounted = true
    setLoading(true)

    dispatch(riskStore.thunks.fetchPublicRiskHistory({ riskId, establishmentId }))
      .unwrap()
      .then((res) => {
        if (!isMounted) return
        setHistory(res)
        setHasHistory(Array.isArray(res) && res.length > 1)
      })
      .catch(() => {
        if (isMounted) {
          setHistory([])
          setHasHistory(false)
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [riskId, dispatch])

  return { history, hasHistory, loading }
}
